import { InfoOutlined } from 'components/icons';
import PageHeader from 'components/PageHeader';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useBreadcrumbLeaf } from 'utils/breadcrumbLeaf';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { categoryPath, ReportEntry } from './reportCatalog';
import { ReportAbout } from './ReportAbout';
import { useReportBaseCurrency } from './reportCurrency';
import { selectedDateBasis } from './reportDateBasis';
import { ReportTags } from './ReportTags';
import { ToneLegendEntry } from './ReportToneLegend';

type Props = {
    report: ReportEntry;
    /** A table report hands the page's height to its list, so the list scrolls and its header stays put. */
    fillHeight?: boolean;
    /** The base currency the report's rows came back in, which its About text names. */
    baseCurrency?: string | null;
    /** The figures the report colours, which the About panel keys; a table report takes them from its columns. */
    toneLegend?: ToneLegendEntry[];
    children: React.ReactNode;
};

/**
 * The frame every report page shares: title, category, tags, the About panel and the report body. The header's date
 * tag is the date the report is filtered by now; the About panel lists every date the report offers.
 */
export function ReportLayout({ report, fillHeight = false, baseCurrency, toneLegend, children }: Props) {
    const [isAboutOpen, setIsAboutOpen] = useState(false);
    const title = t(`reporting.${report.key}.title`);
    const currency = useReportBaseCurrency(baseCurrency);
    const [searchParams] = useSearchParams();
    const dateBasis = selectedDateBasis(report, searchParams);
    // The report as the trail's last crumb, so the Reports crumb before it links back to the list.
    useBreadcrumbLeaf(title);
    return (
        <PaddedLayout className={cn(fillHeight && 'overflow-hidden')}>
            <div className={cn('flex flex-col gap-3', fillHeight && 'h-full')}>
                <PageHeader
                    titleId={`reporting.${report.key}.title`}
                    showBreadcrumb
                    hostsList={fillHeight}
                    icons={
                        <Badge variant="outline" render={<Link to={categoryPath(report.category)} />}>
                            {t(`reporting.category.${report.category}`)}
                        </Badge>
                    }
                    iconsEnd={<ReportTags report={report} dateBases={dateBasis ? [dateBasis] : []} />}
                    buttons={
                        <Button
                            variant={isAboutOpen ? 'secondary' : 'outline'}
                            size="sm"
                            aria-expanded={isAboutOpen}
                            onClick={() => setIsAboutOpen((open) => !open)}
                        >
                            <InfoOutlined className="size-4" />
                            {t('reporting.about-this-report')}
                        </Button>
                    }
                />
                {isAboutOpen && <ReportAbout report={report} baseCurrency={currency} toneLegend={toneLegend} />}
                {children}
            </div>
        </PaddedLayout>
    );
}
